import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useImmerReducer } from 'use-immer';
import { useLoaderData } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import smartReducer from '../../library/Core/SmartReducer';
import { DispatchEvent, State } from '../../library/Core/SmartTypes';
import useManagementDashboard from '../management/dashboard/useManagementDashboard';
import SessionContext from '../../library/Core/SessionContext';
import { GlobalContext } from '../../library/Core/GlobalContext';
import ManageWidgetsListService from './ManageWidgetsListService';
import { getDomainValueForCode } from '../../library/Core/SmartFunctions';
import { addUserSpecificDomains } from '../../library/Service/domainService';
import { useGroupInstituteFilter } from './useGroupInstituteFilter';
import DebugBarControl from '../../library/SimpleControls/DebugBarControl';

const ManageWidgetsList = () => {
    const { globalState } = useContext(GlobalContext);
    const { getCampuses } = useManagementDashboard();
    const { sessionState } = useContext(SessionContext);
    const { groups, getInstitutesByGroup } = useGroupInstituteFilter();
    
    const isSuperAdmin = sessionState?.userTypeCode === 'SUPER_ADMIN';
    
    // State for selected campus and filtered data (for INSTITUTE_ADMIN)
    const [selectedCampus, setSelectedCampus] = useState('');
    
    // State for selected group and institute (for SUPER_ADMIN)
    const [selectedGroup, setSelectedGroup] = useState('');
    const [selectedInstitute, setSelectedInstitute] = useState<any>(null);

    // Cap how many options the menu renders at once. The full institute list can be huge
    // (5k+) and rendering all of them as DOM nodes hangs the page. The user filters via
    // typing — if they want a specific school, they can search for it.
    const INSTITUTE_OPTIONS_CAP = 50;

    // AsyncSelect loader — runs on mount (defaultOptions=true) and on every keystroke.
    // We always recompute from the source so the latest selectedGroup is honoured without
    // needing to remount the select.
    const loadInstituteOptions = useCallback(
        (inputValue: string) =>
            new Promise<any[]>((resolve) => {
                const all = getInstitutesByGroup(selectedGroup || undefined);
                const term = inputValue.trim().toLowerCase();
                const matches = term
                    ? all.filter((opt: any) => opt.label?.toLowerCase().includes(term))
                    : all;
                resolve(matches.slice(0, INSTITUTE_OPTIONS_CAP));
            }),
        [selectedGroup, getInstitutesByGroup],
    );

   const campuses = (sessionState?.userTypeCode === 'INSTITUTE_ADMIN') ?  getCampuses() : globalState.domain.get('INSTITUTE_DOMAIN')?.map((data) => {
        return {label:data.value, value:data.code};
    });

    //console.log(campuses, 'campuses list data')
    const [state, dispatch] = useImmerReducer<State, DispatchEvent>(smartReducer, {
        flags: { isDataLoading: true, showFormErrors: 0 },
        formValidationErrors: {},
        actions: {},
    } as any);

    const { data } = useLoaderData() as { data: State['actions'] };

    //const flexCol = getFlexColDomainValueForCode(alumniData?.instituteId, 'INSTITUTE_DOMAIN', state);
    const domain = addUserSpecificDomains(new Map([...globalState.domain]), data);
    useEffect(() => {
        
        dispatch({
            type: 'DATA_INIT',
            payload: { data, domain},
        });

    }, []);

    /**
     * This function determines the configuration module slug based on the provided widget code.
     * It is used to generate the correct URL for configuring specific widgets.
     *
     * @param widgetCode - The unique identifier of the widget.
     * @returns The configuration module slug corresponding to the given widget code.
     *          If the widget code matches any of the specified cases, the function returns the corresponding configuration module slug.
     *          Otherwise, it defaults to 'configuration'.
     */
    const getConfigurationModuleSlug = (widgetCode: any) => {
        let configurationModule = 'configuration';

        switch (widgetCode) {
            case 'top_careers':
            case 'top_study_country':
            case 'top_companies':
            case 'top_colleges':
            case 'directory_stats':
                configurationModule = 'configure';
                break;

            case 'guiding_alumni_mentors':
            case 'notable_alumni_mentors':
            case 'alumni-speak':
            case 'eclectic_career_alumni':
            case 'entrepreneur_alumni':
            case 'topper_zone':
            case 'spotlight':
                configurationModule = 'configurelist';
                break;
        }

        return configurationModule;
    }

    const [isActive, setIsActive]: any = useState({});

    const handleClick = (id: any) => {
        setIsActive((prevState: any) => ({
            ...prevState,
            [id]: !prevState[id]
        }));
    };

    // Handle campus selection change
    // const handleCampusChange = (e:any) => {
    //     const selectedValue = e.target.value;
    //     setSelectedCampus(selectedValue);

    //     // Filter data based on selected campus
    //     const filtered = campuses.filter((item:any) => item.campus === selectedValue);
    //     setFilteredData(filtered);

    //     ManageWidgetsListService.search(state.data.searchCriteria).then((response: any) => {
    //         setState((prevState) => ({
    //             ...prevState,
    //             data: {
    //                 ...prevState.data,
    //                 wedgitList: response.data, // Update wedgitList in the state
    //             },
    //         }));
    //     });
    // };
    const [widgetState, setWidgetState] = useState<any[]>([]);

    useEffect(() => {
        console.log('Current state:', state.data);
        setWidgetState(state.data?.wedgitList)
    }, [state]);

    const handleCampusChange = (e: any) => {
        const selectedValue = e.target.value; // Get the selected campus
        setSelectedCampus(selectedValue); // Update selected campus state
    
        // Update searchCriteria with the selected campus
        const updatedSearchCriteria = {
            ...state.data.searchCriteria,
            instituteId: selectedValue, // Add or update the campus in the search criteria
        };
    
        // Call the service with the updated searchCriteria
        ManageWidgetsListService.search(updatedSearchCriteria).then((response: any) => {

            console.log(response.data, 'search response data value')
            setWidgetState(response.data)
            
        });
    };

    const handleGroupChange = (e: any) => {
        const groupId = e.target.value;
        setSelectedGroup(groupId);
        setSelectedInstitute(null); // Reset institute selection when group changes

        localStorage.setItem('widgetGroup', groupId);
        localStorage.removeItem('widgetInstitute'); // any previous institute filter is no longer scoped to this group
        // instituteList is derived from selectedGroup via useMemo, so no manual update needed.

        if (!groupId) {
            setWidgetState([]);
            return;
        }

        // Fetch all widgets for the selected group (no institute filter)
        const updatedSearchCriteria = {
            ...state.data.searchCriteria,
            groupId,
            instituteId: '',
        };

        ManageWidgetsListService.search(updatedSearchCriteria).then((response: any) => {
            console.log(response.data, 'search response data value');
            setWidgetState(response.data);
        });
    };

    const handleInstituteChange = (option: any) => {
        setSelectedInstitute(option);

        if (!option) {
            localStorage.removeItem('widgetInstitute');

            // Institute cleared — fall back to group-level widgets if a group is selected
            if (selectedGroup) {
                const updatedSearchCriteria = {
                    ...state.data.searchCriteria,
                    groupId: selectedGroup,
                    instituteId: '',
                };
                ManageWidgetsListService.search(updatedSearchCriteria).then((response: any) => {
                    setWidgetState(response.data);
                });
            } else {
                setWidgetState([]);
            }
            return;
        }

        localStorage.setItem('widgetInstitute', option.value);
        // If user picked an institute WITHOUT a group, clear any stale widgetGroup
        // so the widget config page treats it as a standalone-institute filter.
        if (!selectedGroup) {
            localStorage.removeItem('widgetGroup');
        }

        // Call search with groupId and instituteId
        const updatedSearchCriteria = {
            ...state.data.searchCriteria,
            groupId: selectedGroup || null,
            instituteId: option.value,
        };

        ManageWidgetsListService.search(updatedSearchCriteria).then((response: any) => {
            console.log(response.data, 'search response data value');
            setWidgetState(response.data);
        });
    };

    const instituteValue = (instituteId: any) => {
        let flexCol;
        if (instituteId) {
            flexCol = getDomainValueForCode(instituteId, 'INSTITUTE_DOMAIN', state);
        } else {
            flexCol = '';
        }
        console.log(flexCol, 'flexCol data')
        return flexCol; 
    };

    useEffect(() => {
        // INSTITUTE_ADMIN: auto-select when only one campus is available.
        // SUPER_ADMIN's institute list is derived synchronously via useMemo, so nothing to do here.
        // Guard with !selectedCampus so this only fires once — `campuses` is recomputed
        // (new array reference) on every render, which would otherwise re-trigger this
        // effect on every setWidgetState, causing an infinite getwidgetlist loop.
        if (!isSuperAdmin && campuses?.length === 1 && !selectedCampus) {
            const onlyCampus = campuses[0].value;

            setSelectedCampus(onlyCampus);

            const updatedSearchCriteria = {
                ...state.data?.searchCriteria,
                instituteId: onlyCampus,
            };

            ManageWidgetsListService.search(updatedSearchCriteria)
                .then((response: any) => {
                    setWidgetState(response.data);
                });
        }
    }, [campuses, isSuperAdmin, selectedCampus]);


    return (
        <>
            <div className="main flex-1 bg-white">
                <div className="max-1140 pt-3 pt-sm-4 px-lg-0 pb-5 overflow-hidden mx-auto">
                    <div className="d-flex justify-content-between align-items-center mb-3 pb-3 custom-bottom-liteblue">
                        <h3 data-aos="fade-left" data-aos-delay="600" className="font-35 font-600 mb-0 block-title position-relative">
                            Manage Widgets For Alumni
                        </h3>
                    </div>
                    
                    {state.flags.isDataLoading ? (
                        <div>Loading...</div>
                    ) : (
                        <div className="manage-widgets-wrap">

                            <div className="position-static flex-1 mb-3 dd">
                                <form className="d-flex flex-column flex-sm-row flex-nowrap align-items-sm-center gap-2">
                                    {isSuperAdmin ? (
                                        <>
                                            {/* Institute Group Selection Dropdown for SUPER_ADMIN — narrower since group names are short */}
                                            <select
                                                className="form-select font-13 font-500"
                                                style={{ flex: '0 0 35%' }}
                                                value={selectedGroup}
                                                onChange={handleGroupChange}
                                            >
                                                <option value="">Select Group</option>
                                                {groups?.map((group:any, index:any) => (
                                                    <option key={index} value={group.value}>
                                                        {group.label}
                                                    </option>
                                                ))}
                                            </select>

                                            {/* Institute Autocomplete for SUPER_ADMIN — fills remaining space */}
                                            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                                <AsyncSelect
                                                    key={selectedGroup || 'non-group'}
                                                    cacheOptions
                                                    defaultOptions
                                                    loadOptions={loadInstituteOptions}
                                                    value={selectedInstitute}
                                                    onChange={handleInstituteChange}
                                                    placeholder="Search Institute..."
                                                    isClearable
                                                    // Render menu via portal so the parent's overflow:hidden
                                                    // (.max-1140 wrapper) doesn't clip the dropdown.
                                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                    menuPosition="fixed"
                                                    noOptionsMessage={({ inputValue }: any) =>
                                                        inputValue ? 'No institutes match' : 'Type to search institutes'
                                                    }
                                                    styles={{
                                                        // Match Bootstrap form-control border so it sits beside the group <select>
                                                        control: (base: any, state: any) => ({
                                                            ...base,
                                                            minHeight: 38,
                                                            borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
                                                            boxShadow: state.isFocused
                                                                ? '0 0 0 0.25rem rgba(13,110,253,.25)'
                                                                : 'none',
                                                            '&:hover': {
                                                                borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
                                                            },
                                                        }),
                                                        // Wrap long names so they're fully visible
                                                        option: (base: any) => ({
                                                            ...base,
                                                            whiteSpace: 'normal',
                                                            wordBreak: 'break-word',
                                                            lineHeight: 1.3,
                                                            padding: '8px 12px',
                                                        }),
                                                        singleValue: (base: any) => ({
                                                            ...base,
                                                            whiteSpace: 'normal',
                                                            wordBreak: 'break-word',
                                                        }),
                                                        // 5-10 options visible before scrolling
                                                        menuList: (base: any) => ({
                                                            ...base,
                                                            maxHeight: 420,
                                                        }),
                                                        menuPortal: (base: any) => ({
                                                            ...base,
                                                            zIndex: 9999,
                                                        }),
                                                    }}
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {/* Campus Selection Dropdown for INSTITUTE_ADMIN */}
                                            <select
                                                className="form-select flex-1 font-13 font-500"
                                                value={selectedCampus}
                                                onChange={handleCampusChange}
                                            >
                                                <option value="">Select Campus</option>
                                                {campuses?.map((campus:any, index:any) => (
                                                    <option key={index} value={campus.value}>
                                                        {campus.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </>
                                    )}
                                    {/* <button className="btn btn-search icon icon-search" type="submit"></button> */}
                                </form>
                                {/* <a href="javascript:;" className="btn-search-toggle icon-search"></a> */}
                            </div>
                            {/* SUPER_ADMIN must pick at least a group (institute is optional) before any widget list is shown */}
                            {isSuperAdmin && !selectedGroup && !selectedInstitute ? (
                                <div className="text-center text-muted py-5">
                                    Select a group or institute to view its widgets.
                                </div>
                            ) : (
                            <ul>
                            {Array.isArray(widgetState) && widgetState.map((widget: any) => (
                                    <li key={widget.id} className='p-0'>
                                        <div className="px-3 py-2">
                                            <div className="row g-2 g-md-3 py-1">
                                                <div className="col-md-auto">
                                                    <div className="d-flex h-100 align-items-center justify-content-center"> 
                                                        <i 
                                                        className="icon-down-arrow cursor-pointer" 
                                                        // data-bs-toggle="collapse" 
                                                        // data-bs-target={`#${widget.name.replace(/\s+/g, '')}`} 
                                                        aria-expanded={`${isActive[widget.name.replace(/\s+/g, '')] ? 'false' : 'true'}`}
                                                        // aria-controls={widget.name.replace(/\s+/g, '')} 
                                                        onClick={() => handleClick(widget.name.replace(/\s+/g, ''))}></i>
                                                    </div>
                                                </div>  
                                                <div className="col-md">
                                                    <div className="form-check form-check-tick">
                                                        {/* <input className="form-check-input d-none" type="checkbox" value="" id="Hero" /> */}
                                                        <label className="form-check-label font-18 font-500 flex-1">
                                                            {widget.name}
                                                        </label>
                                                    </div>
                                                </div>
                                                <div className="col-md-auto">
                                                    <div className="d-flex align-items-center">
                                                        {widget.widgetInstId ? (
                                                            <>
                                                                <a href={`/widget/${getConfigurationModuleSlug(widget.code)}/${widget.code}`} className="btn btn-configure me-2">
                                                                    <span>CONFIGURE</span>
                                                                </a>
                                                                {/* <a href={`/widget/${getConfigurationModuleSlug(widget.code)}/${widget.code}/${widget.widgetInstId}`} className="btn btn-widgets-edit me-2">
                                                                    <span>Edit</span>
                                                                </a>
                                                                <a href={`${process.env.REACT_APP_WIDGET_UI_SERVER}/widgetpreview/${widget.widgetInstId}`} target='_blank' className="btn btn-priview">
                                                                    <span>Preview</span> 
                                                                </a> */}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <a href={`/widget/${getConfigurationModuleSlug(widget.code)}/${widget.code}`} className="btn btn-configure">
                                                                    <span>CONFIGURE</span>
                                                                </a>
                                                            </>
                                                        )}
                                                    
                                                    </div>
                                                </div>

                                            </div>
                                            
                                        </div>
                                     
                                        <div className={`collapse ${isActive[widget.name.replace(/\s+/g, '')] ? '' : 'show'}`} >
                                            <div className="p-3 bg-light">
                                                <div className="inner-widget-list-wrap">
                                                
                                                {widget?.sub_list.map((sublist: any) => (
                                                    <div className="widget-list py-2">
                                                    <div className="row g-2 g-md-3 py-1" >                   
                                                    <div className="col-md">
                                                            <div className="form-check form-check-tick h-100 d-flex align-items-center ">
                                                            
                                                                <label className="form-check-label font-18 font-500 flex-1">
                                                                {sublist.widget_name}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="col-md">
                                                            <div className="form-check form-check-tick h-100 d-flex align-items-center ">
                                                            
                                                                <label className="form-check-label font-18 font-500 flex-1">
                                                                {/* {sublist.institute_id} */}
                                                                {instituteValue(sublist.institute_id)}
                                                                
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-auto">
                                                        <div className="d-flex align-items-center">
                                                            <a href={`/widget/${getConfigurationModuleSlug(widget.code)}/${widget.code}/${sublist.widget_inst_id}`} className="btn btn-widgets-edit me-2">
                                                                <span>Edit</span>
                                                            </a>
                                                            <a href={`${process.env.REACT_APP_WIDGET_UI_SERVER}/widgetpreview/${sublist.widget_inst_id}`} target='_blank' className="btn btn-priview">
                                                                <span>Preview</span>
                                                            </a>
                                                        </div>
                                                        </div>
                                                    </div>
                                                    </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        
                                    </li>
                                ))}
                            </ul>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <DebugBarControl />
        </>
    );
};

export default ManageWidgetsList;